import type { NavigationMenuItemExtendedPropsTypes } from 'src/components/Sidepanel/sidepanel-settings-modal';
import {
	getSettingsRailRowTitle,
	isSettingsRailItemAdvanced,
	type FlattenedRailEntry,
	type SettingsRailSectionId,
} from './flatten_settings_rail';

export const MIN_SETTINGS_SEARCH_QUERY_LENGTH = 2;

type SettingsSearchTranslate = (key: string, options: { default: string }) => string;

export type SettingsSearchContentKind = 'heading' | 'label' | 'option' | 'description';

export type SettingsSearchContentCatalogEntry = {
	key: string;
	default: string;
	kind: SettingsSearchContentKind;
	order: number;
};

export type SettingsSearchContentCatalog = Record<string, SettingsSearchContentCatalogEntry[]>;

export type SettingsSearchContentEntry = SettingsSearchContentCatalogEntry & {
	text: string;
};

export type SettingsSearchDocument = {
	item: NavigationMenuItemExtendedPropsTypes;
	title: string;
	sectionTitle: string;
	aliases: string;
	content: SettingsSearchContentEntry[];
	advanced: boolean;
	order: number;
	sectionId?: SettingsRailSectionId;
};

export type SettingsSearchMatch = SettingsSearchDocument & {
	matchedContent?: SettingsSearchContentEntry;
};

export type SettingsSearchTextSegment = {
	text: string;
	highlighted: boolean;
};

const SETTINGS_SEARCH_ALIASES: Record<string, string> = {
	details: 'device name room image icon',
	device_information: 'model device id mac hardware information',
	maintenance_actions: 'restart reboot reset factory transfer delete',
	firmware_version: 'update software version',
	wifi_mode_client: 'wifi wi-fi wireless network internet ssid',
	wifi_client_backup: 'wifi wi-fi wireless network internet ssid fallback',
	wifi_mode_ap: 'wifi wi-fi wireless network hotspot access point',
	cloud: 'remote connection internet',
	mqtt: 'broker integration messaging',
	bluetooth_settings: 'bluetooth ble wireless',
	bluetooth_gateway: 'bluetooth ble wireless gateway',
	authentication: 'password login security protection',
	restrict_login: 'password login security protection',
	geo_location_and_time_zone: 'location timezone time zone coordinates latitude longitude',
	input_output: 'input output switch button relay',
	timer: 'timer automation auto on off countdown',
	schedule: 'schedule timer automation calendar',
};

export const normalizeSettingsSearchText = (value: string): string =>
	value
		.normalize('NFKD')
		.replace(/[\u0300-\u036f]/g, '')
		.toLowerCase()
		.replace(/[^\p{L}\p{N}]+/gu, ' ')
		.trim()
		.replace(/\s+/g, ' ');

const getAlias = (title: string): string => SETTINGS_SEARCH_ALIASES[title] ?? '';

const getPlainSearchContentText = (value: string): string =>
	value
		.replace(/<[^>]*>/g, ' ')
		.replace(/\s+/g, ' ')
		.trim();

const getSnippetText = (value: string, queryTokens: string[]): string => {
	const maxLength = 140;
	if (value.length <= maxLength) return value;

	const firstMatchedWord = [...value.matchAll(/[\p{L}\p{N}]+/gu)].find((match) =>
		queryTokens.includes(normalizeSettingsSearchText(match[0])),
	);
	let start = Math.max(0, (firstMatchedWord?.index ?? 0) - 40);
	let end = Math.min(value.length, start + maxLength - 2);
	if (start > 0) {
		const nextWordBoundary = value.indexOf(' ', start);
		if (nextWordBoundary > -1 && nextWordBoundary < (firstMatchedWord?.index ?? end)) {
			start = nextWordBoundary + 1;
		}
	}
	if (end < value.length) {
		const previousWordBoundary = value.lastIndexOf(' ', end);
		if (previousWordBoundary > (firstMatchedWord?.index ?? start)) end = previousWordBoundary;
	}
	return `${start > 0 ? '…' : ''}${value.slice(start, end).trim()}${end < value.length ? '…' : ''}`;
};

export const getSettingsSearchTextSegments = (
	text: string,
	query: string,
): SettingsSearchTextSegment[] => {
	const queryTokens = new Set(normalizeSettingsSearchText(query).split(' ').filter(Boolean));
	return text
		.split(/([\p{L}\p{N}]+)/gu)
		.filter(Boolean)
		.map((segment) => ({
			text: segment,
			highlighted: queryTokens.has(normalizeSettingsSearchText(segment)),
		}));
};

const createDocument = (
	item: NavigationMenuItemExtendedPropsTypes,
	translate: SettingsSearchTranslate,
	contentCatalog: SettingsSearchContentCatalog,
	order: number,
	sectionId?: SettingsRailSectionId,
	sectionTitle = '',
): SettingsSearchDocument => {
	const rowTitle = getSettingsRailRowTitle(item);
	const searchComponentKey = item.component?.searchComponentKey ?? item.component?.componentKey;
	const content =
		typeof searchComponentKey === 'string'
			? (contentCatalog[searchComponentKey] ?? []).map((entry) => ({
					...entry,
					text: getPlainSearchContentText(translate(entry.key, { default: entry.default })),
				}))
			: [];
	return {
		item,
		title: translate(rowTitle.key, { default: rowTitle.default }),
		sectionTitle,
		aliases: getAlias(item.title),
		content,
		advanced: isSettingsRailItemAdvanced(item, sectionId),
		order,
		sectionId,
	};
};

export const buildSettingsSearchDocuments = (
	entries: FlattenedRailEntry[],
	detailsItem: NavigationMenuItemExtendedPropsTypes | undefined,
	translate: SettingsSearchTranslate,
	sectionTitles: Partial<Record<SettingsRailSectionId, string>>,
	contentCatalog: SettingsSearchContentCatalog = {},
): SettingsSearchDocument[] => {
	const documents: SettingsSearchDocument[] = [];
	let order = 0;

	if (detailsItem && !detailsItem.disabled) {
		documents.push(createDocument(detailsItem, translate, contentCatalog, order));
		order += 1;
	}

	for (const entry of entries) {
		if (entry.kind === 'row') {
			if (!entry.item.disabled) {
				documents.push(createDocument(entry.item, translate, contentCatalog, order));
				order += 1;
			}
			continue;
		}

		const sectionTitle = sectionTitles[entry.id] ?? '';
		for (const item of entry.items) {
			if (item.disabled) continue;
			documents.push(
				createDocument(item, translate, contentCatalog, order, entry.id, sectionTitle),
			);
			order += 1;
		}
	}

	return documents;
};

const hasAllWordMatches = (text: string, queryTokens: string[]): boolean => {
	const words = new Set(normalizeSettingsSearchText(text).split(' '));
	return queryTokens.every((token) => words.has(token));
};

const contentKindRank: Record<SettingsSearchContentKind, number> = {
	heading: 0,
	label: 1,
	option: 2,
	description: 3,
};

const getMatchedContent = (
	document: SettingsSearchDocument,
	queryTokens: string[],
): SettingsSearchContentEntry | undefined => {
	return document.content
		.map((entry) => {
			const words = new Set(normalizeSettingsSearchText(entry.text).split(' '));
			const matchedTokenCount = queryTokens.filter((token) => words.has(token)).length;
			return { entry, matchedTokenCount };
		})
		.filter(({ matchedTokenCount }) => matchedTokenCount > 0)
		.sort(
			(first, second) =>
				second.matchedTokenCount - first.matchedTokenCount ||
				contentKindRank[first.entry.kind] - contentKindRank[second.entry.kind] ||
				first.entry.order - second.entry.order,
		)[0]?.entry;
};

const contentIncludesQuery = (
	document: SettingsSearchDocument,
	query: string,
	kinds: SettingsSearchContentKind[],
): boolean =>
	document.content.some(
		(entry) => kinds.includes(entry.kind) && hasAllWordMatches(entry.text, query.split(' ')),
	);

const getMatchRank = (document: SettingsSearchDocument, query: string): number => {
	const title = normalizeSettingsSearchText(document.title);
	const aliases = normalizeSettingsSearchText(document.aliases);
	const sectionTitle = normalizeSettingsSearchText(document.sectionTitle);
	const queryTokens = query.split(' ');

	if (title === query) return 0;
	if (title.startsWith(`${query} `)) return 1;
	if (hasAllWordMatches(title, queryTokens)) return 2;
	if (contentIncludesQuery(document, query, ['heading', 'label', 'option'])) return 3;
	if (hasAllWordMatches(aliases, queryTokens)) return 4;
	if (contentIncludesQuery(document, query, ['description'])) return 5;
	if (hasAllWordMatches(sectionTitle, queryTokens)) return 6;
	return 7;
};

export const searchSettingsDocuments = (
	documents: SettingsSearchDocument[],
	query: string,
): SettingsSearchMatch[] => {
	const normalizedQuery = normalizeSettingsSearchText(query);
	if (normalizedQuery.length < MIN_SETTINGS_SEARCH_QUERY_LENGTH) return [];

	const queryTokens = normalizedQuery.split(' ');
	return documents
		.filter((document) => {
			const searchableText = `${document.title} ${document.sectionTitle} ${document.aliases} ${document.content
				.map((entry) => entry.text)
				.join(' ')}`;
			return hasAllWordMatches(searchableText, queryTokens);
		})
		.sort((first, second) => {
			const rankDifference =
				getMatchRank(first, normalizedQuery) - getMatchRank(second, normalizedQuery);
			return rankDifference || first.order - second.order;
		})
		.map((document) => {
			const matchedContent = getMatchedContent(document, queryTokens);
			return {
				...document,
				...(matchedContent && getMatchRank(document, normalizedQuery) > 2
					? {
							matchedContent: {
								...matchedContent,
								text: getSnippetText(matchedContent.text, queryTokens),
							},
						}
					: {}),
			};
		});
};

export const filterSettingsRailEntries = (
	entries: FlattenedRailEntry[],
	matches: SettingsSearchMatch[],
): FlattenedRailEntry[] => {
	const matchOrder = new Map(matches.map((match, index) => [match.item, index]));
	const filteredEntries: FlattenedRailEntry[] = [];

	for (const entry of entries) {
		if (entry.kind === 'row') {
			if (matchOrder.has(entry.item)) filteredEntries.push(entry);
			continue;
		}

		const items = entry.items
			.filter((item) => matchOrder.has(item))
			.sort(
				(first, second) =>
					(matchOrder.get(first) ?? Number.MAX_SAFE_INTEGER) -
					(matchOrder.get(second) ?? Number.MAX_SAFE_INTEGER),
			);
		if (items.length > 0) filteredEntries.push({ ...entry, items });
	}

	return filteredEntries;
};
